import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { getPlatformSettingsRaw, updatePlatformSettings } from '@/lib/api';
import { Save, Loader2, Eye, EyeOff, Key, Mail, Globe, Cpu } from 'lucide-react';

const AI_PROVIDERS = [
  { value: 'gemini', label: 'Google Gemini' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'anthropic', label: 'Anthropic' },
];

const AI_MODELS = {
  gemini: [
    { value: 'gemini-3-flash-preview', label: 'Gemini 3 Flash (empfohlen)' },
    { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
    { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
    { value: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash' },
  ],
  openai: [
    { value: 'gpt-5.2', label: 'GPT-5.2' },
    { value: 'gpt-4o', label: 'GPT-4o' },
    { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
  ],
  anthropic: [
    { value: 'claude-sonnet-4-5-20250929', label: 'Claude Sonnet 4.5' },
    { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5' },
  ],
};

export function PlatformSettingsTab() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showKeys, setShowKeys] = useState({});

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPlatformSettingsRaw();
      setSettings(res.data);
    } catch {
      toast.error(t('admin.platform.loadError'));
    } finally { setLoading(false); }
  }, [t]);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updatePlatformSettings(settings);
      toast.success(t('admin.platform.savedSuccess'));
      loadSettings();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('admin.common.saveError'));
    } finally { setSaving(false); }
  };

  const update = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));
  const toggleShow = (key) => setShowKeys(prev => ({ ...prev, [key]: !prev[key] }));

  if (loading || !settings) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  const availableModels = AI_MODELS[settings.ai_provider] || AI_MODELS.gemini;

  return (
    <div className="space-y-6 max-w-3xl">
      {/* AI Configuration */}
      <Card data-testid="settings-ai-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Cpu className="h-4 w-4 text-violet-600" />
            KI-Konfiguration
          </CardTitle>
          <CardDescription className="text-xs">
            {t('admin.platform.aiSettingsDesc')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-medium">KI-Anbieter</Label>
              <Select value={settings.ai_provider} onValueChange={(v) => {
                update('ai_provider', v);
                update('ai_model', AI_MODELS[v]?.[0]?.value || '');
              }}>
                <SelectTrigger data-testid="settings-ai-provider">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AI_PROVIDERS.map(p => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium">KI-Modell</Label>
              <Select value={settings.ai_model} onValueChange={(v) => update('ai_model', v)}>
                <SelectTrigger data-testid="settings-ai-model">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableModels.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs font-medium">KI API-Key (Emergent LLM Key)</Label>
            <div className="flex gap-2">
              <Input
                type={showKeys.ai_api_key ? 'text' : 'password'}
                value={settings.ai_api_key || ''}
                onChange={(e) => update('ai_api_key', e.target.value)}
                placeholder="sk-emergent-..."
                data-testid="settings-ai-key"
              />
              <Button variant="ghost" size="icon" onClick={() => toggleShow('ai_api_key')} className="shrink-0">
                {showKeys.ai_api_key ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">{t('admin.platform.geminiHint')}</p>
          </div>
        </CardContent>
      </Card>

      {/* Email Configuration */}
      <Card data-testid="settings-email-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Mail className="h-4 w-4 text-orange-600" />
            E-Mail-Konfiguration (SMTP)
          </CardTitle>
          <CardDescription className="text-xs">
            SMTP-Server für den Mail-Versand (z. B. Strato: smtp.strato.de · Port 465 · SSL).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <Label className="text-xs font-medium">SMTP-Host</Label>
              <Input
                value={settings.smtp_host || ''}
                onChange={(e) => update('smtp_host', e.target.value)}
                placeholder="smtp.strato.de"
                data-testid="settings-smtp-host"
              />
            </div>
            <div>
              <Label className="text-xs font-medium">Port</Label>
              <Input
                type="number"
                value={settings.smtp_port ?? 465}
                onChange={(e) => update('smtp_port', parseInt(e.target.value || '0', 10))}
                placeholder="465"
                data-testid="settings-smtp-port"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="smtp-ssl"
              type="checkbox"
              checked={!!settings.smtp_use_ssl}
              onChange={(e) => update('smtp_use_ssl', e.target.checked)}
              data-testid="settings-smtp-ssl"
              className="h-4 w-4 rounded border-gray-300"
            />
            <Label htmlFor="smtp-ssl" className="text-xs font-medium cursor-pointer">
              SSL/TLS verwenden (Port 465). Deaktivieren für STARTTLS (Port 587).
            </Label>
          </div>
          <div>
            <Label className="text-xs font-medium">SMTP-Benutzer</Label>
            <Input
              value={settings.smtp_user || ''}
              onChange={(e) => update('smtp_user', e.target.value)}
              placeholder="postfach@deine-domain.de"
              data-testid="settings-smtp-user"
            />
          </div>
          <div>
            <Label className="text-xs font-medium">SMTP-Passwort</Label>
            <div className="flex gap-2">
              <Input
                type={showKeys.smtp_password ? 'text' : 'password'}
                value={settings.smtp_password || ''}
                onChange={(e) => update('smtp_password', e.target.value)}
                placeholder="••••••••"
                data-testid="settings-smtp-password"
              />
              <Button variant="ghost" size="icon" onClick={() => toggleShow('smtp_password')} className="shrink-0">
                {showKeys.smtp_password ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          <div>
            <Label className="text-xs font-medium">Absender-E-Mail</Label>
            <Input
              value={settings.sender_email || ''}
              onChange={(e) => update('sender_email', e.target.value)}
              placeholder="Servicedesk@surplify.de"
              data-testid="settings-sender-email"
            />
          </div>
        </CardContent>
      </Card>

      {/* Application URLs */}
      <Card data-testid="settings-urls-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Globe className="h-4 w-4 text-blue-600" />
            Anwendungs-URLs
          </CardTitle>
          <CardDescription className="text-xs">
            {t('admin.platform.appUrlDesc')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs font-medium">Anwendungs-URL</Label>
            <Input
              value={settings.app_url || ''}
              onChange={(e) => update('app_url', e.target.value)}
              placeholder="https://your-domain.com"
              data-testid="settings-app-url"
            />
            <p className="text-[10px] text-muted-foreground mt-1">{t('admin.platform.baseUrlHint')}</p>
          </div>
          <div>
            <Label className="text-xs font-medium">NVIDIA API Base URL</Label>
            <Input
              value={settings.nvidia_api_base || ''}
              onChange={(e) => update('nvidia_api_base', e.target.value)}
              placeholder="https://integrate.api.nvidia.com/v1"
              data-testid="settings-nvidia-base"
            />
          </div>
          <div>
            <Label className="text-xs font-medium">NVIDIA API-Key</Label>
            <div className="flex gap-2">
              <Input
                type={showKeys.nvidia_api_key ? 'text' : 'password'}
                value={settings.nvidia_api_key || ''}
                onChange={(e) => update('nvidia_api_key', e.target.value)}
                placeholder="nvapi-..."
                data-testid="settings-nvidia-key"
              />
              <Button variant="ghost" size="icon" onClick={() => toggleShow('nvidia_api_key')} className="shrink-0">
                {showKeys.nvidia_api_key ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="bg-orange-500 hover:bg-orange-600" data-testid="settings-save-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          {t('common.save')}
        </Button>
      </div>
    </div>
  );
}
